/**
 * Tiny color utilities for the furniture renderer. Everything works on
 * `#RRGGBB` hex strings so it composes with the existing `Furniture.color`.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): Rgb {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 148, g: 163, b: 184 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const c = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Linear mix of two hex colors, `t` in [0,1] toward `b`. */
export function mix(a: string, b: string, t: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  const k = Math.max(0, Math.min(1, t));
  return rgbToHex({
    r: A.r + (B.r - A.r) * k,
    g: A.g + (B.g - A.g) * k,
    b: A.b + (B.b - A.b) * k,
  });
}

/** Lighten (`amount` > 0) or darken (`amount` < 0) by mixing toward white/black. */
export function shade(hex: string, amount: number): string {
  if (amount >= 0) return mix(hex, "#ffffff", amount);
  return mix(hex, "#000000", -amount);
}

/** `rgba()` string from hex + alpha. */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

/** Perceived luminance in [0,1]. */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** Pick a readable label color for text drawn on top of `bg`. */
export function labelColorFor(bg: string): string {
  return luminance(bg) > 0.62 ? "rgba(20,20,28,0.85)" : "rgba(255,255,255,0.92)";
}
